import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { X } from '@phosphor-icons/react'
import { router } from '@inertiajs/react'
import { Button, Empty, IconButton } from './ui'
import { Sheet, ThinkingIndicator } from './interior'
import { Toolbar } from './workspace'
import type { TaskSheetData } from './TaskSheet'

type OverlayState = {
  path: string
  open: boolean
  data: TaskSheetData | null
  error: string | null
}

const TASK_PATH = /^\/spaces\/[^/]+\/tasks\/[^/]+\/?$/
const loadTaskSheet = () => import('./TaskSheet')
const TaskSheetPanel = lazy(() => loadTaskSheet().then((module) => ({ default: module.TaskSheetPanel })))

function taskPathFromClick(event: MouseEvent) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null
  const target = event.target instanceof Element ? event.target : null
  const anchor = target?.closest<HTMLAnchorElement>('a[href]')
  if (!anchor || anchor.hasAttribute('download') || anchor.dataset.taskNavigation === 'page') return null
  if (anchor.target && anchor.target !== '_self') return null
  const url = new URL(anchor.href, window.location.href)
  if (url.origin !== window.location.origin || !TASK_PATH.test(url.pathname)) return null
  return url.pathname.replace(/\/$/, '')
}

// Fills the sheet that is already open while the task is fetched, so waiting never costs a second
// disclosure animation.
function OpeningTask({ state, onClose, onRetry }: { state: OverlayState; onClose: () => void; onRetry: () => void }) {
  return (
    <>
      <Toolbar
        stacked
        className="border-b border-line px-3 sm:px-4"
        leading={<IconButton label="Close" onClick={onClose}><X className="size-4" weight="bold" aria-hidden="true" /></IconButton>}
        title={state.error ? 'Couldn’t open this' : 'Opening…'}
      />
      <div className="grid min-h-0 flex-1 place-items-center p-6">
        {state.error ? (
          <Empty className="max-w-sm">
            <span>{state.error}</span>
            <span className="flex justify-center gap-2 pt-3">
              <Button variant="secondary" onClick={onRetry}>Try again</Button>
              <Button as="a" href={state.path} data-task-navigation="page">Open full page</Button>
            </span>
          </Empty>
        ) : <ThinkingIndicator name="Opening" />}
      </div>
    </>
  )
}

// This boundary stays mounted while Inertia swaps pages. It owns every in-app task disclosure,
// so Home, notifications, chats, and Spaces all keep their exact background and scroll position.
export type TaskOverlayProviderProps = {
  children?: ReactNode
}

export default function TaskOverlayProvider({ children }: TaskOverlayProviderProps) {
  const background = useRef<HTMLDivElement>(null)
  const request = useRef<AbortController | null>(null)
  const [state, setState] = useState<OverlayState | null>(null)

  const load = useCallback(async (path: string, { quiet = false }: { quiet?: boolean } = {}) => {
    loadTaskSheet()
    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    setState((current) => ({
      path,
      open: quiet && current?.path === path ? current.open : true,
      data: quiet && current?.path === path ? current.data : null,
      error: null,
    }))

    try {
      const response = await fetch(`${path}.json`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
      const body = await response.json().catch(() => ({})) as Partial<TaskSheetData> & { error?: string; redirect?: string }
      if (controller.signal.aborted || request.current !== controller) return
      // Sunny's tasks are system admins' (TasksController#require_visible_task): anyone else
      // goes to the conversation the work reports into.
      if (body.redirect?.startsWith('/')) {
        setState(null)
        router.visit(body.redirect)
        return
      }
      if (!response.ok) throw new Error(body.error || (response.status === 404 ? 'This is no longer available.' : 'This could not be loaded.'))
      if (!body.task || !body.space) throw new Error('This could not be loaded. Sign in and try again.')
      const data = body as TaskSheetData
      setState((current) => current?.path === path ? { ...current, data, error: null } : current)
    } catch (error) {
      if ((error as Error).name === 'AbortError') return
      setState((current) => current?.path === path ? { ...current, error: (error as Error).message || 'This could not be loaded.' } : current)
    }
  }, [])

  useEffect(() => {
    const openTask = (event: MouseEvent) => {
      const path = taskPathFromClick(event)
      if (!path) return
      event.preventDefault()
      load(path)
    }
    document.addEventListener('click', openTask, true)
    return () => {
      document.removeEventListener('click', openTask, true)
      request.current?.abort()
    }
  }, [load])

  const setOpen = useCallback((open: boolean) => setState((current) => current ? { ...current, open } : current), [])
  const finishClose = useCallback(() => {
    request.current?.abort()
    setState(null)
  }, [])
  const activePath = state?.path
  const refresh = useCallback(() => {
    if (activePath) load(activePath, { quiet: true })
  }, [activePath, load])
  const task = state?.data?.task

  return (
    <>
      <div ref={background} className="contents">{children}</div>
      {/* One sheet owns the whole disclosure. Remounting it — when the task arrives, or when its
          chunk finishes loading — would replay the opening animation as a flash and a second open. */}
      <Sheet
        open={Boolean(state?.open)}
        onOpenChange={setOpen}
        onExitComplete={finishClose}
        backgroundRef={background}
        viewport
        label={task ? `${task.title} ${task.kind === 'manual' ? 'to-do' : 'task'}` : 'Details'}
      >
        {state && (
          <Suspense fallback={<OpeningTask state={state} onClose={() => setOpen(false)} onRetry={refresh} />}>
            {state.data ? (
              <TaskSheetPanel
                key={state.path}
                {...state.data}
                open={state.open}
                onOpenChange={setOpen}
                onRefresh={refresh}
                overlay
              />
            ) : <OpeningTask state={state} onClose={() => setOpen(false)} onRetry={refresh} />}
          </Suspense>
        )}
      </Sheet>
    </>
  )
}
